import React, { useState } from 'react';
import { toast } from 'sonner';
import { contentAPI } from '../../utils/api';
import { track } from '../../utils/analytics';

// "2026-06-29" -> "June 29, 2026"
const formatNiceDate = (iso) => {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
  } catch { return iso; }
};

const InquiryForm = ({ data }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', eventDate: '',
    eventType: '', guestCount: '', message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guard: enforce earliest_event_date locally as well (input min is the
    // primary UX; this catches edge cases like manually typed dates).
    if (data.earliest_event_date && formData.eventDate && formData.eventDate < data.earliest_event_date) {
      toast.error(`Event date must be on or after ${formatNiceDate(data.earliest_event_date)}.`);
      return;
    }
    setSubmitting(true);
    try {
      await contentAPI.submitEventInquiry({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        event_date: formData.eventDate,
        event_type: formData.eventType,
        guest_count: formData.guestCount,
        message: formData.message,
      });
      // Fire high-intent Lead conversion across all browser-side trackers.
      // (Server-side CAPI fires from the backend submit endpoint with the
      // same inquiry_id to dedupe against this.)
      track('Lead', {
        content_name: 'Private Event Inquiry',
        event_type: formData.eventType,
        guest_count: formData.guestCount,
      });
      toast.success(data.success_message || 'Thank you! Our events team will contact you within 24 hours.');
      setFormData({ name: '', email: '', phone: '', eventDate: '', eventType: '', guestCount: '', message: '' });
    } catch (err) {
      toast.error(err.message || 'Could not submit inquiry. Please try again or email reservations@bricklanela.com.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-5xl mb-6" style={{ fontFamily: 'var(--font-serif)' }}>
          {data.title || 'Plan Your Event'}
        </h2>
        {data.subtitle && <p className="text-xl text-gray-700">{data.subtitle}</p>}
      </div>

      {data.notice && (
        <div
          className="mb-10 border-l-4 border-[#C9A86C] bg-[#FBF6EC] text-gray-900 px-5 py-4 rounded-r-lg"
          role="status"
          data-testid="inquiry-notice"
        >
          <p className="text-sm md:text-base leading-relaxed">{data.notice}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-10 rounded-2xl shadow-xl" data-testid="inquiry-form">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">Name *</label>
            <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A86C] transition-all" />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2">Email *</label>
            <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A86C] transition-all" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium mb-2">Phone *</label>
            <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A86C] transition-all" />
          </div>
          <div>
            <label htmlFor="eventDate" className="block text-sm font-medium mb-2">Event Date *</label>
            <input
              type="date"
              id="eventDate"
              name="eventDate"
              required
              min={data.earliest_event_date || undefined}
              value={formData.eventDate}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A86C] transition-all"
            />
            {data.earliest_event_date && (
              <p className="text-xs text-gray-500 mt-1.5" data-testid="earliest-date-caption">
                Earliest available: {formatNiceDate(data.earliest_event_date)}
              </p>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="eventType" className="block text-sm font-medium mb-2">Event Type *</label>
            <select id="eventType" name="eventType" required value={formData.eventType} onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A86C] transition-all">
              <option value="">Select...</option>
              <option value="corporate">Corporate Event</option>
              <option value="wedding">Wedding</option>
              <option value="birthday">Birthday</option>
              <option value="anniversary">Anniversary</option>
              <option value="catering">Off-Site Catering</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label htmlFor="guestCount" className="block text-sm font-medium mb-2">Guest Count *</label>
            <input type="number" id="guestCount" name="guestCount" required min="1" value={formData.guestCount} onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A86C] transition-all" />
          </div>
        </div>
        <div>
          <label htmlFor="message" className="block text-sm font-medium mb-2">Tell us about your event *</label>
          <textarea id="message" name="message" required rows="5" value={formData.message} onChange={handleChange}
            placeholder="Share your vision, special requests, dietary needs, budget considerations..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A86C] transition-all resize-none" />
        </div>
        <button type="submit" className="btn-primary w-full text-lg py-4" data-testid="inquiry-submit-btn">
          Submit Inquiry
        </button>
        {data.footer_note && <p className="text-sm text-gray-600 text-center">{data.footer_note}</p>}
      </form>
    </div>
  );
};

export default InquiryForm;
